// @react-pdf-viewer 依存を排除した自前の型定義

import type { CSSProperties } from 'react'

import type { PdfTheme } from '@/store/useUISettingsStore'

import type { DragMode, ReaderLayout } from '../../shared/reader-layout'

/** スクロールモード */
export const ScrollMode = {
  Vertical: 'vertical',
  Horizontal: 'horizontal',
  Page: 'page',
} as const
export type ScrollMode = (typeof ScrollMode)[keyof typeof ScrollMode]

/** テキスト方向 */
export const TextDirection = {
  LeftToRight: 'ltr',
  RightToLeft: 'rtl',
} as const
export type TextDirection = (typeof TextDirection)[keyof typeof TextDirection]

/** ビューモード */
export const ViewMode = {
  SinglePage: 'single',
  DualPage: 'dual',
  DualPageWithCover: 'dual-cover',
} as const
export type ViewMode = (typeof ViewMode)[keyof typeof ViewMode]

/** 特殊ズームレベル */
export const SpecialZoomLevel = {
  PageWidth: 'page-width',
  PageHeight: 'page-height',
} as const
export type SpecialZoomLevel = (typeof SpecialZoomLevel)[keyof typeof SpecialZoomLevel]

export type ZoomLevel = number | SpecialZoomLevel

/** 見開きのページ間隔 (px) */
export const DUAL_GAP = 4

export type PdfSettings = {
  scrollMode: ScrollMode
  viewMode: ViewMode
  zoom: ZoomLevel
  direction: TextDirection
  dragMode: DragMode
}

export const DEFAULT_PDF_SETTINGS: PdfSettings = {
  scrollMode: ScrollMode.Vertical,
  viewMode: ViewMode.SinglePage,
  zoom: SpecialZoomLevel.PageWidth,
  direction: TextDirection.LeftToRight,
  dragMode: 'free',
}

/** 読み方に合うズームのプリセット。ページめくりは 1 ページが画面に収まる大きさにする */
export const defaultZoomFor = ({ scrollMode, dragMode }: Pick<PdfSettings, 'scrollMode' | 'dragMode'>) =>
  scrollMode === ScrollMode.Horizontal || dragMode === 'snap' ? SpecialZoomLevel.PageHeight : SpecialZoomLevel.PageWidth

/** 作品ごとに覚える表示設定（ピンチ等の数値ズームは覚えない） */
export type PdfLayout = Pick<PdfSettings, 'scrollMode' | 'viewMode' | 'direction' | 'dragMode'> & {
  zoom: SpecialZoomLevel
}

export const toPdfLayout = (settings: PdfSettings): PdfLayout => ({
  scrollMode: settings.scrollMode,
  viewMode: settings.viewMode,
  direction: settings.direction,
  dragMode: settings.dragMode,
  zoom: typeof settings.zoom === 'number' ? defaultZoomFor(settings) : settings.zoom,
})

/** コミックと共通の読み方の設定に直す。縦スクロールでは direction が見開きの並びを表す */
export const toReaderLayout = (settings: PdfSettings): ReaderLayout => ({
  direction: settings.scrollMode === ScrollMode.Horizontal ? settings.direction : 'ttb',
  dragMode: settings.dragMode,
  pageMode: settings.viewMode,
  dualReadingOrder: settings.direction,
})

/** 読み方の設定を反映する。スクロール方向か送り方が変わったらズームも合わせる */
export const applyReaderLayout = (settings: PdfSettings, layout: ReaderLayout): PdfSettings => {
  const isHorizontal = layout.direction !== 'ttb'
  const next: PdfSettings = {
    ...settings,
    scrollMode: isHorizontal ? ScrollMode.Horizontal : ScrollMode.Vertical,
    direction: layout.direction === 'ttb' ? layout.dualReadingOrder : layout.direction,
    viewMode: layout.pageMode,
    dragMode: layout.dragMode,
  }
  if (next.scrollMode !== settings.scrollMode || next.dragMode !== settings.dragMode) {
    next.zoom = defaultZoomFor(next)
  }
  return next
}

export const ZOOM_PRESETS: { value: SpecialZoomLevel; label: string; title: string }[] = [
  { value: SpecialZoomLevel.PageWidth, label: '幅', title: '幅に合わせる' },
  { value: SpecialZoomLevel.PageHeight, label: '縦', title: '縦に合わせる' },
]

export const PDF_THEME_OPTIONS: { value: PdfTheme; label: string }[] = [
  { value: 'light', label: 'ライト' },
  { value: 'sepia', label: 'セピア' },
  { value: 'dark', label: 'ダーク' },
]

export const DEFAULT_PDF_THEME: PdfTheme = 'light'
export const DEFAULT_PDF_WARMTH = 0

type Rgb = readonly [number, number, number]

// ページに乗算で重ねる色。白はこの色になり、黒は黒のまま
const WHITE: Rgb = [255, 255, 255]
const SEPIA_PAPER: Rgb = [244, 236, 216]
// 暖色 100% のときに重ねる色
const WARM_MAX: Rgb = [255, 180, 110]

const getThemeBase = (theme: PdfTheme): { tint: Rgb; paper?: string; background?: string } => {
  switch (theme) {
    case 'sepia':
      return { tint: SEPIA_PAPER, paper: '#ffffff', background: '#2b2520' }
    case 'dark':
      // canvas を invert(0.9) したときの白の色に揃える
      return { tint: WHITE, paper: '#1a1a1a', background: '#0d0d0d' }
    case 'light':
    default:
      return { tint: WHITE }
  }
}

/** 配色と暖色の強さから、リーダーのルートに当てるスタイルを求める */
export const getPdfThemeStyle = (theme: PdfTheme, warmth: number): CSSProperties => {
  const { tint, paper, background } = getThemeBase(theme)
  const ratio = Math.min(100, Math.max(0, warmth)) / 100
  const mix = (base: number, warm: number) => Math.round((base * (255 - (255 - warm) * ratio)) / 255)
  const [r, g, b] = tint
  const [wr, wg, wb] = WARM_MAX
  return {
    '--pdf-tint': `rgb(${mix(r, wr)} ${mix(g, wg)} ${mix(b, wb)})`,
    ...(paper && { '--pdf-paper': paper }),
    backgroundColor: background,
  } as CSSProperties
}

/** ページに色を重ねる必要があるか */
export const isPdfTinted = (theme: PdfTheme, warmth: number): boolean => theme === 'sepia' || warmth > 0
